import api from './api';
import { moduleFromPath } from './moduleFromPath';

// Action-level usage tracking — the layer below the module-visit ping in Layout.js.
//
// A module visit tells us an agent opened Tickets. It cannot tell us which tab
// they used, which filter they left on, or which button they reached for — which
// is exactly what you need before redesigning a screen that carries ~44% of all
// internal navigation. `track()` fills that gap.
//
// Design constraints:
//  - A click must never wait on the network → actions are buffered and flushed.
//  - Telemetry must never break the app → every failure path is swallowed.
//  - No entity ids, ever → `target` is a descriptive slug (a tab name, a filter
//    value), never a ticket number or a company id. The backend enforces the
//    same shape, so a mistake here is dropped rather than stored.

const FLUSH_MS = 15000;
const MAX_BUFFER = 40; // matches MAX_PER_FLUSH in routes/uiActions.js

let buffer = [];
let timer = null;

// One id per page load (per tab). Identifies a BROWSER SESSION, not a person —
// the user is already on every row. Without it there is no notion of a path,
// and every interesting question is a path question: what did they try before
// this, what did they open and abandon, how many steps to get the job done.
// `seq` exists because two actions in the same millisecond are common (a click
// that both closes a menu and navigates) and timestamps can't order them.
// Exported so utils/heatmap.js stamps raw clicks with the SAME session id —
// a dead click and the ui_actions around it must be joinable into one story.
export const SESSION_ID = (() => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* fall through */ }
  // Older Safari has no randomUUID; the backend validates UUID shape, so a
  // hand-rolled v4 is required rather than any random string.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
})();
let seqCounter = 0;

function flush() {
  if (timer) { clearTimeout(timer); timer = null; }
  if (buffer.length === 0) return;
  const payload = { actions: buffer.slice(0, MAX_BUFFER) };
  buffer = [];
  // Sin token no hay nada que mandar: el POST daría 401 y, peor, arrastraría al
  // usuario a la pantalla de login. `_telemetry` además le dice al interceptor
  // que este request no toca la sesión pase lo que pase.
  try {
    if (!localStorage.getItem('accessToken')) return;
  } catch { return; }
  api.post('/ui-actions', payload, { _telemetry: true }).catch(() => {});
}

/**
 * Record one UI action.
 * @param {string} module  module key, as used by module_visits (e.g. 'tickets')
 * @param {string} action  what happened, as a slug (e.g. 'tab_open', 'filter_set')
 * @param {string} [target] optional descriptive slug (e.g. 'messages', 'status_open').
 *                          NEVER an id or free text.
 * @param {object} [opts]
 * @param {'ok'|'fail'|'abandon'} [opts.result] outcome. Pass 'fail' from catch
 *   blocks: an action recorded only when it succeeds makes its own failure rate
 *   invisible, which is the failure mode this whole table exists to avoid.
 * @param {number} [opts.durationMs] how long it took / how long they stayed.
 */
export function track(module, action, target, opts = {}) {
  try {
    if (!module || !action) return;
    buffer.push({
      module,
      action,
      target: target || null,
      sessionId: SESSION_ID,
      seq: seqCounter++,
      result: opts.result || null,
      durationMs: Number.isFinite(opts.durationMs) ? opts.durationMs : null,
    });
    if (buffer.length >= MAX_BUFFER) { flush(); return; }
    if (!timer) timer = setTimeout(() => flush(), FLUSH_MS);
  } catch {
    // never let instrumentation throw into a click handler
  }
}

// ── Dwell: where the effort actually goes ──────────────────────────────────
// Page opens are the wrong unit. A module opened twice for twenty minutes each
// matters more than one opened forty times for five seconds, and every ranking
// built on visit counts says the opposite. Dwell fixes the denominator.
//
// Time only accrues while the tab is VISIBLE. A helpdesk tab sits open all day
// behind other windows; counting that as time-on-Tickets would make the busiest
// module look busier for the one reason that has nothing to do with the work.

let dwellModule = null;
let dwellStart = null;   // ms timestamp, or null while paused
let dwellAccrued = 0;    // ms banked across visibility pauses

function accrue() {
  if (dwellStart !== null) {
    dwellAccrued += Date.now() - dwellStart;
    dwellStart = null;
  }
}

function emitDwell() {
  accrue();
  // Sub-second dwell is a redirect or a bounce through a route, not a visit.
  if (dwellModule && dwellAccrued >= 1000) {
    track(dwellModule, 'module_dwell', null, { durationMs: dwellAccrued });
  }
  dwellModule = null;
  dwellAccrued = 0;
}

/** Called by Layout on every module change. Closes the previous module's dwell. */
export function startDwell(moduleKey) {
  if (moduleKey === dwellModule) return;
  emitDwell();
  dwellModule = moduleKey || null;
  dwellAccrued = 0;
  dwellStart = dwellModule ? Date.now() : null;
}

// ── Friction tracking ──────────────────────────────────────────────────────
// Usage alone can't tell you where to spend effort: a screen opened 3.000 times
// that works is not the same problem as one opened 300 times that fails. Errors
// are attributed to the module the agent was LOOKING AT, not to the API path —
// "Tickets broke on me" is the answer a redesign needs, and one screen routinely
// calls half a dozen different endpoints.
//
// Nothing about the request or the error message is recorded: only the status
// class. This must stay a health signal, never a shadow log of what agents do.

const ERROR_DEDUP_MS = 60000;
const recentErrors = new Map();

function trackFriction(action, target) {
  // A broken poll retries every few seconds and would otherwise drown out every
  // other signal in the table — one row per distinct failure per minute.
  const key = `${action}|${target}`;
  const now = Date.now();
  const last = recentErrors.get(key);
  if (last && now - last < ERROR_DEDUP_MS) return;
  recentErrors.set(key, now);
  if (recentErrors.size > 100) recentErrors.clear();

  const mod = moduleFromPath(typeof window !== 'undefined' ? window.location.pathname : '');
  if (!mod) return; // login screen, portal, anything outside the module map
  track(mod, action, target);
}

if (typeof window !== 'undefined') {
  window.addEventListener('api-error', (e) => {
    const status = e?.detail?.status ?? 0;
    // Bucketed, not exact: 500 and 503 are the same story, and a per-code
    // breakdown would fragment the counts without changing any decision.
    const bucket =
      status === 0 ? 'network'
      : status === 403 ? 'forbidden'
      : status === 404 ? 'not_found'
      : status >= 500 ? 'server_error'
      : status >= 400 ? 'client_error'
      : 'other';
    trackFriction('api_error', bucket);
  });

  // Uncaught JS errors are already buffered by useConsoleErrorBuffer, but that
  // buffer is only ever READ when someone manually opens the bug-report modal —
  // so today a crash is only known if an agent bothers to report it.
  window.addEventListener('error', () => trackFriction('js_error', 'uncaught'));
  window.addEventListener('unhandledrejection', () => trackFriction('js_error', 'unhandled_rejection'));
}

// Flush on tab-hide so a long idle tab doesn't sit on a buffer for hours.
// Deliberately NOT sendBeacon: beacons can't carry the Authorization header, so
// they'd 401 and drop the batch silently — worse than losing the last few
// actions of a hard page-unload, which is all this misses.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      // Bank the time so far and stop the clock — a backgrounded tab is not work.
      accrue();
      flush();
    } else if (dwellModule && dwellStart === null) {
      dwellStart = Date.now();
    }
  });
  // Best-effort close of the final dwell. If the browser kills the flush on
  // unload we lose one row; the alternative (sendBeacon) can't authenticate.
  window.addEventListener('pagehide', () => { emitDwell(); flush(); });
}

export default track;
